import React, { useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useFetchData } from "../../hooks/useStableFetchData";
import { API_URL } from "../../utils/constant";
import { useEffect } from "react";
import { PaginationStockBalance } from "../../components/PaginationStockBalance";

const FROZEN_COLUMN_WIDTHS = {
  code: 150,
  name: 260,
  category: 220,
  unit: 220,
  warehouse: 150,
};

const stickyLeft = {
  code: 0,
  name: FROZEN_COLUMN_WIDTHS.code,
  category: FROZEN_COLUMN_WIDTHS.code + FROZEN_COLUMN_WIDTHS.name,
  unit:
    FROZEN_COLUMN_WIDTHS.code +
    FROZEN_COLUMN_WIDTHS.name +
    FROZEN_COLUMN_WIDTHS.category,
};

export default function StockBalanceList() {
  const tableContainerRef = useRef(null);
  const [limit] = useState(10);
  // stable reference (won't change across renders)
  const defaultQuery = useMemo(() => ({ status: "ACTIVE" }), []);

  const { data, setCurrentPage } = useFetchData({
    url: API_URL + "/stock-balance/list",
    defaultQuery,
  });

  // no need to mirror page locally; read it from the hook:
  const page = data.page ?? 1;

  const handlePageChange = (newPage) => {
    // Let the hook drive page & fetching. No query/reload here.
    setCurrentPage(newPage);
  };

  const payload = data?.results ?? {};
  const stockBalanceData = {
    stock_balance: payload.stock_balance ?? [],
    warehouses: payload.warehouses ?? [],
  };

  const warehouseColumns = stockBalanceData?.warehouses?.flatMap(
    (mainWarehouse) => mainWarehouse?.warehouse_list ?? []
  );

  return (
    <>
      <div className="page-layout">
        <div className="page-header">
          <h1>Stock Balance List</h1>
        </div>

        <div className="container w-full mt-10 col-span-full">
          <div
            ref={tableContainerRef}
            className="w-[100%] mx-auto cursor-grab active:cursor-grabbing overflow-x-auto hide-scrollbar"
          >
            <table className="bg-white custom-table border-separate border-spacing-0">
              <colgroup>
                <col style={{ width: `${FROZEN_COLUMN_WIDTHS.code}px` }} />
                <col style={{ width: `${FROZEN_COLUMN_WIDTHS.name}px` }} />
                <col style={{ width: `${FROZEN_COLUMN_WIDTHS.category}px` }} />
                <col style={{ width: `${FROZEN_COLUMN_WIDTHS.unit}px` }} />
                {warehouseColumns.map((warehouse) => (
                  <col
                    key={warehouse.warehouse_id}
                    style={{ width: `${FROZEN_COLUMN_WIDTHS.warehouse}px` }}
                  />
                ))}
              </colgroup>
              <thead className="h-12 py-3 text-xs font-light text-netural-600 bg-neutral-100 text-nowrap">
                <tr>
                  <th
                    className="sticky z-20 px-4 py-3 text-left text-xs font-light border-b border-r border-gray-300 bg-neutral-200"
                    style={{
                      left: `${stickyLeft.code}px`,
                      minWidth: `${FROZEN_COLUMN_WIDTHS.code}px`,
                    }}
                  >
                    Product Code
                  </th>
                  <th
                    className="sticky z-20 px-4 py-3 text-left text-xs font-light border-b border-r border-gray-300 bg-neutral-200"
                    style={{
                      left: `${stickyLeft.name}px`,
                      minWidth: `${FROZEN_COLUMN_WIDTHS.name}px`,
                    }}
                  >
                    Product Name
                  </th>
                  <th
                    className="sticky z-20 px-4 py-3 text-left text-xs font-light border-b border-r border-gray-300 bg-neutral-200"
                    style={{
                      left: `${stickyLeft.category}px`,
                      minWidth: `${FROZEN_COLUMN_WIDTHS.category}px`,
                    }}
                  >
                    Product Category
                  </th>
                  <th
                    className="sticky z-20 px-4 py-3 text-left text-xs font-light border-b border-r border-gray-300 bg-neutral-200"
                    style={{
                      left: `${stickyLeft.unit}px`,
                      minWidth: `${FROZEN_COLUMN_WIDTHS.unit}px`,
                    }}
                  >
                    Converted Unit | Based Unit
                  </th>

                  {warehouseColumns.map((warehouse) => (
                    <th
                      key={warehouse.warehouse_id}
                      className="min-w-[150px] px-4 py-3 text-center text-xs font-light border-b border-r border-gray-300 bg-neutral-100 align-bottom"
                    >
                      <div className="font-normal text-primary-dark break-words">
                        {warehouse.warehouse_name}
                      </div>
                      <div className="mt-2 border-t border-gray-200 pt-2 text-xs text-gray-500">
                        Stock Qty
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* @ts-ignore */}
                {stockBalanceData?.stock_balance?.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-100 borders">
                    <td
                      className="sticky z-10 px-4 py-4 border-b border-r border-gray-300 text-primary-dark bg-neutral-200 align-top"
                      style={{
                        left: `${stickyLeft.code}px`,
                        minWidth: `${FROZEN_COLUMN_WIDTHS.code}px`,
                      }}
                    >
                      {row.product_code}
                    </td>
                    <td
                      className="sticky z-10 px-4 py-4 border-b border-r border-gray-300 bg-neutral-200 align-top"
                      style={{
                        left: `${stickyLeft.name}px`,
                        minWidth: `${FROZEN_COLUMN_WIDTHS.name}px`,
                      }}
                    >
                      {row.product_name}
                    </td>
                    <td
                      className="sticky z-10 px-4 py-4 border-b border-r border-gray-300 bg-neutral-200 align-top"
                      style={{
                        left: `${stickyLeft.category}px`,
                        minWidth: `${FROZEN_COLUMN_WIDTHS.category}px`,
                      }}
                    >
                      {row.product_category_name}
                    </td>
                    <td
                      className="sticky z-10 px-4 py-4 border-b border-r border-gray-300 bg-neutral-200 align-top"
                      style={{
                        left: `${stickyLeft.unit}px`,
                        minWidth: `${FROZEN_COLUMN_WIDTHS.unit}px`,
                      }}
                    >
                      <span>{row.converted_unit_name}</span>
                      <span className="px-2">|</span>
                      <span>{row.base_unit_name}</span>
                    </td>

                    {warehouseColumns.map((warehouse) => {
                      const targetDetail = row?.details?.find(
                        (detail) => detail.warehouse_id === warehouse.warehouse_id
                      );

                      return (
                        <td
                          key={warehouse.warehouse_id}
                          className="min-w-[150px] px-4 py-4 text-center border-b border-r border-gray-300 align-top"
                        >
                          <div className="flex items-center justify-center whitespace-nowrap">
                            <span>{targetDetail?.convertedBalanceQty || 0}</span>
                            <span className="px-2 text-gray-400">|</span>
                            <span>{targetDetail?.stockQty || 0}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="w-full flex justify-end mt-4">
            <PaginationStockBalance
              data={{
                page: page,
                totalCount: data?.totalCount || 0,
                limit: data?.limit || limit,
              }}
              setCurrentPage={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
}
